import React, { useCallback, useMemo, useRef, useState } from 'react';
import { type InputBaseComponentProps } from '@mui/material/InputBase';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { IMask, IMaskInput } from 'react-imask';
import { useLocale } from '../hooks';

type DateBlockKey = 'yyyy' | 'mm' | 'dd';

export type DateMaskEventTarget = {
	value: string;
	unmaskedValue: string;
	typedValue: Date | null;
};

export type DateMaskEvent = {
	target: DateMaskEventTarget;
};

export type DateMaskProps = Omit<InputBaseComponentProps, 'onChange'> & {
	formatter: Intl.DateTimeFormat;
	onChange?: (event: DateMaskEvent) => void;
	showDigitMask?: boolean;
};

const DateMask = React.forwardRef<HTMLInputElement, DateMaskProps>(
	function DateMask(
		{ onChange, formatter, showDigitMask = false, ...inputProps },
		ref
	) {
		const [value, setValue] = useState('');
		const previousFinalValueRef = useRef('');

		const handleAccept = useCallback(
			(value: string, maskRef: InstanceType<typeof IMask.InputMask>) => {
				const unmaskedValue = maskRef.unmaskedValue;
				const typedValue = maskRef.typedValue as Date | null;

				const target: DateMaskEventTarget = {
					value,
					unmaskedValue,
					typedValue,
				};

				setValue(value);

				if (previousFinalValueRef.current === value) {
					return;
				}

				onChange?.({ target });

				previousFinalValueRef.current = unmaskedValue;
			},
			[onChange]
		);

		/**
		 * This is really only here to prevent warning logs, since it breaks
		 * convention. Instead, we set the input value within the onAccept handler
		 */
		const handleChange = useCallback((): void => {
			return;
		}, []);

		const { pattern, format, parse } = useMemo(() => {
			const placeholders: Map<Intl.DateTimeFormatPart['type'], DateBlockKey> =
				new Map([
					['year', 'yyyy'],
					['month', 'mm'],
					['day', 'dd'],
				]);

			const parts = formatter.formatToParts(new Date(1900, 0, 30));

			const pattern = parts
				.map(
					(part: Intl.DateTimeFormatPart) =>
						placeholders.get(part.type) ?? part.value
				)
				.join('');

			const indices = new Map<DateBlockKey, [number, number]>(
				Array.from(placeholders.values()).map(placeholder => {
					const index = pattern.indexOf(placeholder);

					return [placeholder, [index, index + placeholder.length]];
				})
			);

			return {
				pattern,
				format: (date: Date | null): string => {
					if (!date) return '';
					return formatter.format(date);
				},
				parse: (dateString: string): Date | null => {
					if (!dateString) return null;
					const [yearStart, yearEnd] = indices.get('yyyy') ?? [];
					const [monthStart, monthEnd] = indices.get('mm') ?? [];
					const [dateStart, dateEnd] = indices.get('dd') ?? [];
					const params = [];

					if (yearStart === undefined || yearEnd === undefined) {
						console.warn(
							`failed to parse year from ${dateString} using pattern ${pattern}. Passing date-string directly to constructor.`
						);
						return new Date(dateString);
					}

					params.push(Number.parseInt(dateString.slice(yearStart, yearEnd)));

					if (monthStart === undefined || monthEnd === undefined) {
						console.warn(
							`failed to parse month from ${dateString} using pattern ${pattern}. Passing date-string directly to constructor.`
						);
						return new Date(dateString);
					}

					params.push(
						Number.parseInt(dateString.slice(monthStart, monthEnd)) - 1
					);

					if (dateStart === undefined || dateEnd === undefined) {
						console.warn(
							`failed to parse date of month from ${dateString} using pattern ${pattern}. Passing date-string directly to constructor.`
						);
						return new Date(dateString);
					}

					params.push(Number.parseInt(dateString.slice(dateStart, dateEnd)));

					return new Date(...(params as []));
				},
			};
		}, [formatter]);

		return (
			<IMaskInput
				{...inputProps}
				value={value}
				mask={Date}
				pattern={pattern}
				blocks={{
					// @ts-ignore -- the key is in the pattern, but ts makes this way more difficult than it needs to be
					yyyy: {
						mask: IMask.MaskedRange,
						from: 1900,
						to: 9999,
					},
					// @ts-ignore -- the key is in the pattern, but ts makes this way more difficult than it needs to be
					mm: {
						mask: IMask.MaskedRange,
						from: 1,
						to: 12,
					},
					// @ts-ignore -- the key is in the pattern, but ts makes this way more difficult than it needs to be
					dd: {
						mask: IMask.MaskedRange,
						from: 1,
						to: 31,
					},
				}}
				format={format}
				parse={parse}
				inputRef={ref}
				onAccept={handleAccept}
				onChange={handleChange}
				lazy={!showDigitMask}
				overwrite
			/>
		);
	}
);

const placeholderMap = new Map<Intl.DateTimeFormatPartTypes, string>([
	['year', 'yyyy'],
	['month', 'mm'],
	['day', 'dd'],
]);

export type DateInputProps = Omit<TextFieldProps, 'onChange'> &
	Pick<DateMaskProps, 'onChange'>;

export function DateInput({
	onChange,
	...textFieldProps
}: DateInputProps): React.JSX.Element {
	const { InputProps, ...otherTextFieldProps } = textFieldProps;
	const locale = useLocale();
	const formatter = useMemo(
		() =>
			new Intl.DateTimeFormat(locale, {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			}),
		[locale]
	);

	const placeholder: string = useMemo(() => {
		const parts = formatter.formatToParts(new Date());

		return parts
			.map(part =>
				part.type === 'literal'
					? part.value
					: placeholderMap.get(part.type) ?? part.value
			)
			.join('');
	}, [formatter]);

	return (
		<TextField
			{...otherTextFieldProps}
			onChange={onChange as TextFieldProps['onChange']}
			type="search"
			InputProps={{
				...InputProps,
				inputComponent: DateMask,
			}}
			inputProps={{ formatter }}
			helperText={placeholder}
		/>
	);
}
